/**
 * Form-fill data fetchers. Loads form + record + submission for the
 * FormFillPanel and normalizes the form definition snapshot. Kept separate
 * from the React component so the data-shape can be tested independently
 * and reused by other surfaces (e.g. the standalone submission route).
 */
import { supabase } from '@/lib/supabase/client'
import type { UploadedFileRef } from '../api/file-upload-api'
import type {
  FormDefinition,
  FieldDefinition,
  ConditionalRule,
} from '@/features/form-builder/api/form-builder-api'

export function parseTitle(v: unknown): string {
  if (typeof v === 'string') return v
  if (v && typeof v === 'object') return (v as Record<string, string>).fa ?? ''
  return ''
}

export function isUploadedFileRef(v: unknown): v is UploadedFileRef {
  return !!v && typeof v === 'object' && 'storagePath' in v && 'bucket' in v
}

export interface FormFillData {
  form: {
    id: string
    title: unknown
    instance_type: string
    current_version_id: string | null
    group_id: string
  }
  definition: FormDefinition
  submission: Record<string, unknown> | null
  recordDisplayId: string
  recordStatus: string
  projectId: string
  formTitle: string
  versionId: string
  renderVersionId: string
  pinnedVersionLabel: string
  isPinnedOldVersion: boolean
}

export async function fetchFormData(
  formId: string,
  recordId: string,
  instanceIndex: number,
  submissionId?: string,
): Promise<FormFillData> {
  let submissionQuery = supabase
    .from('submissions')
    .select(
      'id, status, field_values, version, instance_index, form_version_id, revision_reason, revision_field_keys, soft_warning_flags',
    )
    .eq('is_deleted', false)

  if (submissionId) {
    submissionQuery = submissionQuery.eq('id', submissionId)
  } else {
    submissionQuery = submissionQuery
      .eq('record_id', recordId)
      .eq('form_id', formId)
      .eq('instance_index', instanceIndex)
  }

  const [formRes, recordRes, { data: submission }] = await Promise.all([
    supabase
      .from('forms')
      .select('id, title, instance_type, current_version_id, group_id')
      .eq('id', formId)
      .single(),
    supabase
      .from('records')
      .select('display_id, status, project_id')
      .eq('id', recordId)
      .single(),
    submissionQuery.maybeSingle(),
  ])
  if (formRes.error) throw formRes.error
  if (!formRes.data.current_version_id) {
    throw new Error('این فرم هنوز منتشر نشده. ابتدا فرم را در فرم‌ساز منتشر کنید.')
  }

  const renderVersionId = submission?.form_version_id ?? formRes.data.current_version_id

  const { data: version, error: vErr } = await supabase
    .from('form_versions')
    .select('definition_snapshot, version_major, version_minor')
    .eq('id', renderVersionId)
    .single()
  if (vErr) throw vErr

  const raw = version.definition_snapshot as Record<string, unknown>
  let definition: FormDefinition
  if (Array.isArray(raw?.fields)) {
    // Migrate legacy `action_type` → `type` for action shape compatibility
    const rules = ((raw.conditional_rules as ConditionalRule[]) ?? []).map((rule) => ({
      ...rule,
      actions: rule.actions.map((a) => {
        const r = a as Record<string, unknown>
        if (r.action_type !== undefined && r.type === undefined) {
          return { ...a, type: r.action_type as (typeof a)['type'] }
        }
        return a
      }),
    }))
    definition = { ...(raw as unknown as FormDefinition), conditional_rules: rules }
  } else {
    const steps = (raw?.steps as Array<Record<string, unknown>> | undefined) ?? []
    definition = {
      fields: steps.flatMap((s) => (s.fields as FieldDefinition[] | undefined) ?? []),
      conditional_rules: [],
      calculated_fields: [],
    }
  }

  const isPinnedOldVersion =
    !!submission?.form_version_id &&
    submission.form_version_id !== formRes.data.current_version_id

  return {
    form: formRes.data,
    definition,
    submission: submission as Record<string, unknown> | null,
    recordDisplayId: recordRes.data?.display_id ?? recordId,
    recordStatus: recordRes.data?.status as string,
    projectId: recordRes.data?.project_id as string,
    formTitle: parseTitle(formRes.data.title),
    versionId: formRes.data.current_version_id,
    renderVersionId,
    pinnedVersionLabel: `${version.version_major}.${version.version_minor}`,
    isPinnedOldVersion,
  }
}

/**
 * The instance_index a new instance of this repeatable form should take.
 *
 * Deliberately counts soft-deleted rows. `submissions_single_instance_unique`
 * is UNIQUE (record_id, form_id, instance_index) and is *not* partial, so a
 * discarded draft goes on occupying its index forever. Filtering `is_deleted`
 * out here means deleting the newest instance hands its index straight back:
 * with 1,2,3 and #3 discarded, this returned 3 and the insert died on 23505.
 *
 * So indexes are monotonic and never reused, which also keeps them honest as
 * audit references — a discarded #3 and a later #3 must not be the same label.
 * The cost is gaps (1, 2, 4), which is correct rather than tidy.
 */
export async function getNextInstanceIndex(
  formId: string,
  recordId: string,
): Promise<number> {
  const { data } = await supabase
    .from('submissions')
    .select('instance_index')
    .eq('form_id', formId)
    .eq('record_id', recordId)
    .order('instance_index', { ascending: false })
    .limit(1)
  return (data?.[0]?.instance_index ?? 0) + 1
}
